import { useRef } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useSeo, SITE_URL } from "@/lib/seo";
import { LOCATIONS, getLocation } from "@/data/locations";
import IMAGES from "@/data/images";
import { Breadcrumbs } from "@/components/site/PageHeader";
import { SectionLabel } from "@/components/site/SectionLabel";
import { StatusBadge } from "@/components/site/StatusBadge";
import { Reveal } from "@/components/site/Reveal";
import { ContextualCTA } from "@/components/site/ContextualCTA";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function LocationDetail() {
  const { slug } = useParams();
  const reduce = useReducedMotion();
  const loc = getLocation(slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  useSeo(
    loc
      ? {
          title: `${loc.name} Property Intelligence`,
          description: `${loc.name} property intelligence — terrain, connectivity, planning, land regulation and due diligence. ${loc.tagline}`,
          path: `/locations/${slug}`,
          image: (IMAGES[loc.image] || IMAGES.heroRidge).url,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: `${loc.name} Property Intelligence`,
            about: { "@type": "Place", name: loc.name, address: "Uttarakhand, India" },
            url: `${SITE_URL}/locations/${slug}`,
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
                { "@type": "ListItem", position: 2, name: "Locations", item: `${SITE_URL}/locations` },
                { "@type": "ListItem", position: 3, name: loc.name, item: `${SITE_URL}/locations/${slug}` },
              ],
            },
          },
        }
      : { title: "Not found", path: `/locations/${slug}` }
  );

  if (!loc) return <Navigate to="/locations" replace />;
  const img = IMAGES[loc.image] || IMAGES.heroRidge;

  const others = LOCATIONS.filter((l) => l.slug !== slug).slice(0, 4);

  return (
    <>
      {/* Cinematic hero */}
      <section ref={heroRef} className="relative h-[82vh] min-h-[560px] overflow-hidden" data-testid="location-hero">
        <motion.img
          src={img.url}
          alt={img.alt}
          style={reduce ? {} : { y: imgY, scale: imgScale }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#10110f]/85 via-[#10110f]/20 to-[#10110f]/35" />
        <div className="relative h-full ss-container flex flex-col justify-end pb-16">
          <div className="text-[#F7F5F0]">
            <Breadcrumbs items={[{ label: "Locations", to: "/locations" }, { label: loc.name }]} />
            <p className="font-mono-ss text-[0.7rem] tracking-widest text-[#F7F5F0]/70 mb-4">{loc.coords} · {loc.elevation}</p>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl font-light leading-[0.9]">{loc.name}</h1>
            <p className="mt-4 ss-overline text-[#C2A374]">{loc.region}</p>
            <p className="mt-6 max-w-2xl text-lg sm:text-xl font-light text-[#F7F5F0]/80 leading-relaxed">{loc.tagline}</p>
          </div>
        </div>
      </section>

      {/* Quick facts */}
      <section className="bg-[#EFECE6]" data-testid="location-quickfacts">
        <div className="ss-container grid grid-cols-2 md:grid-cols-4 divide-x divide-[#E2DDD5] border-b border-[#E2DDD5]">
          {loc.quickFacts.map((f) => (
            <div key={f.label} className="px-5 py-8 first:pl-0">
              <p className="ss-overline text-[#8A9098] mb-2">{f.label}</p>
              <p className="font-display text-xl text-[#1A1C1E] leading-snug">{f.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Intro + sections */}
      <section className="ss-container py-20 lg:py-28 grid lg:grid-cols-[0.9fr_1.4fr] gap-12 lg:gap-20">
        <aside className="lg:sticky lg:top-28 self-start">
          <SectionLabel marker="SS·LOC" className="mb-5">Location Intelligence</SectionLabel>
          <p className="font-display text-2xl sm:text-3xl font-light leading-snug text-[#1A1C1E]">{loc.intro}</p>
          <nav className="mt-8 space-y-2" aria-label="On this page">
            {loc.sections.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="block ss-link text-[0.85rem] text-[#52575D]">{s.heading}</a>
            ))}
          </nav>
        </aside>

        <div>
          {loc.sections.map((s, i) => (
            <Reveal key={s.id} delay={Math.min(i * 0.04, 0.2)}>
              <div id={s.id} className="scroll-mt-28 pb-12 mb-12 border-b border-[#E2DDD5] last:border-0">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <h2 className="font-display text-2xl sm:text-3xl font-normal text-[#1A1C1E]">{s.heading}</h2>
                  <StatusBadge status={s.status} />
                </div>
                {s.body.map((p, j) => (
                  <p key={j} className="text-[1.05rem] leading-[1.8] font-light text-[#3a3f44] mb-4">{p}</p>
                ))}
                {s.list && (
                  <ul className="mt-5 space-y-2.5">
                    {s.list.map((it, k) => (
                      <li key={k} className="flex gap-3 text-[1rem] font-light text-[#3a3f44]">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#8C6A53]" />{it}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>
          ))}

          {/* Due diligence accordion */}
          <div id="due-diligence" className="scroll-mt-28">
            <div className="flex items-center justify-between gap-4 mb-5">
              <h2 className="font-display text-2xl sm:text-3xl font-normal text-[#1A1C1E]">Due-diligence questions</h2>
              <StatusBadge status="draft" />
            </div>
            <Accordion type="single" collapsible className="border-t border-[#E2DDD5]">
              {loc.dueDiligence.map((q, i) => (
                <AccordionItem key={i} value={`q-${i}`} className="border-b border-[#E2DDD5]">
                  <AccordionTrigger className="text-left font-display text-lg sm:text-xl font-normal text-[#1A1C1E] hover:no-underline py-5" data-testid={`dd-q-${i}`}>
                    <span className="flex gap-4">
                      <span className="font-mono-ss text-[0.7rem] text-[#C2A374] mt-1.5">{String(i + 1).padStart(2, "0")}</span>
                      {q}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-[0.98rem] font-light text-[#52575D] leading-relaxed pl-9 pb-5">
                    A question worth answering before you commit. We can help you obtain and interpret the evidence for this during a buyer-side assessment.
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <ContextualCTA
        eyebrow={loc.name}
        title={`Evaluating a property in ${loc.name}?`}
        text={`Discuss the location with Selling Sunsets — an independent, buyer-side reading of what ${loc.name} is really like to buy into.`}
        whatsappMessage={`Hi Suhail, I'm evaluating a property in ${loc.name} and would like to discuss the location.`}
      />

      {/* Other locations */}
      <section className="ss-container pb-28">
        <h2 className="font-display text-3xl font-light text-[#1A1C1E] mb-10">Other locations</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
          {others.map((l) => (
            <Link key={l.slug} to={`/locations/${l.slug}`} className="group" data-testid={`other-location-${l.slug}`}>
              <div className="ss-hover-grow overflow-hidden aspect-[3/4] relative bg-[#EFECE6]">
                <img src={(IMAGES[l.image] || IMAGES.heroRidge).url} alt={l.name} loading="lazy" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#181A1B]/70 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <h3 className="font-display text-2xl text-[#F7F5F0] leading-none">{l.name}</h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
